import { Link } from 'react-router-dom';
import {
  ArrowRight,
  BrainCircuit,
  FileStack,
  MapPinned,
  Calculator,
  HardHat,
  TrendingUp,
  Paintbrush,
  Sparkles,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/store/useAuth';

const SIDE_BENEFITS = [
  {
    icon: FileStack,
    title: 'Every home report',
    desc: 'Property history, disclosures, and condition intel—unlocked on any listing.',
  },
  {
    icon: MapPinned,
    title: 'Neighborhood & market',
    desc: 'See comps, demand, and whether buying in that area looks profitable.',
  },
];

const GRID_BENEFITS = [
  {
    icon: Calculator,
    title: 'Renovation costs',
    desc: 'Model remodel budgets before you bid—room by room.',
  },
  {
    icon: HardHat,
    title: 'Verified contractor quotes',
    desc: 'Turn estimates into real quotes from vetted pros.',
  },
  {
    icon: TrendingUp,
    title: 'Profit & ROI analysis',
    desc: 'Average upside after purchase and improvements—clear numbers.',
  },
  {
    icon: Paintbrush,
    title: 'Virtual redesign',
    desc: 'Preview new interiors and finishes before you commit.',
  },
];

export function PremiumBenefits() {
  const isAuthenticated = useAuth((s) => s.isAuthenticated);
  const ctaTo = isAuthenticated ? '/dashboard/billing' : '/register';
  const ctaState = isAuthenticated ? undefined : { from: '/dashboard/billing' };

  return (
    <section
      aria-labelledby="premium-benefits-heading"
      className="relative overflow-hidden bg-slate-950 py-16 sm:py-20"
      data-testid="home-premium-benefits"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse 80% 60% at 10% 0%, rgba(58,125,182,0.35) 0%, transparent 55%), radial-gradient(ellipse 50% 40% at 90% 100%, rgba(24,52,81,0.55) 0%, transparent 50%)',
        }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage:
            'linear-gradient(rgba(255,255,255,0.7) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.7) 1px, transparent 1px)',
          backgroundSize: '48px 48px',
          maskImage: 'radial-gradient(ellipse 70% 60% at 50% 40%, black, transparent)',
        }}
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="reveal flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
          <div className="max-w-2xl">
            <p className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-sky-300/90">
              <Sparkles size={14} aria-hidden />
              Casahom Premium
            </p>
            <h2
              id="premium-benefits-heading"
              className="font-display mt-3 text-3xl font-semibold tracking-tight text-white sm:text-4xl"
            >
              What Premium unlocks
            </h2>
            <p className="mt-2 max-w-xl text-base text-slate-300 sm:text-lg">
              Decision-grade tools for every home you consider—before you buy, remodel, or walk away.
            </p>
          </div>
          <Button
            asChild
            className="w-fit shrink-0 rounded-full bg-white text-blue-700 hover:bg-sky-50"
          >
            <Link to={ctaTo} state={ctaState}>
              Start free for 7 days
              <ArrowRight size={16} className="ml-1.5" aria-hidden />
            </Link>
          </Button>
        </div>

        {/* Bento */}
        <div className="reveal mt-10 grid grid-cols-1 gap-4 lg:grid-cols-12 lg:gap-5">
          {/* Featured */}
          <article className="group relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-blue-600/90 via-blue-700 to-slate-900 p-6 sm:p-8 lg:col-span-7 lg:row-span-2 lg:min-h-[22rem]">
            <div
              aria-hidden
              className="absolute -right-8 -top-8 h-40 w-40 rounded-full bg-sky-400/20 blur-3xl transition-opacity duration-500 group-hover:opacity-100 opacity-70"
            />
            <div className="relative flex h-full flex-col justify-between gap-8">
              <div>
                <div className="grid h-12 w-12 place-items-center rounded-xl bg-white/15 text-white ring-1 ring-white/20">
                  <BrainCircuit size={24} aria-hidden />
                </div>
                <h3 className="font-display mt-5 text-2xl font-semibold tracking-tight text-white sm:text-3xl">
                  AI-powered home analysis
                </h3>
                <p className="mt-3 max-w-md text-sm leading-relaxed text-sky-100/90 sm:text-base">
                  Instant, listing-level insight on condition, risk signals, and opportunity—so you
                  know what you&rsquo;re walking into before the tour.
                </p>
              </div>
              <ul className="flex flex-wrap gap-2" aria-label="Analysis highlights">
                {['Condition signals', 'Risk flags', 'Opportunity score'].map((tag) => (
                  <li
                    key={tag}
                    className="rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-medium text-white/90"
                  >
                    {tag}
                  </li>
                ))}
              </ul>
            </div>
          </article>

          {SIDE_BENEFITS.map((b) => (
            <article
              key={b.title}
              className="group rounded-2xl border border-white/10 bg-white/[0.04] p-5 transition-colors duration-300 hover:bg-white/[0.07] sm:p-6 lg:col-span-5"
            >
              <div className="grid h-10 w-10 place-items-center rounded-xl bg-sky-400/15 text-sky-300 ring-1 ring-sky-300/20 transition-transform duration-300 group-hover:-translate-y-0.5">
                <b.icon size={20} aria-hidden />
              </div>
              <h3 className="mt-4 text-base font-semibold text-white">{b.title}</h3>
              <p className="mt-1.5 text-sm leading-relaxed text-slate-400">{b.desc}</p>
            </article>
          ))}
        </div>

        <div className="reveal mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 lg:gap-5">
          {GRID_BENEFITS.map((b) => (
            <article
              key={b.title}
              className="group rounded-2xl border border-white/10 bg-white/[0.04] p-5 transition-colors duration-300 hover:border-sky-400/30 hover:bg-white/[0.07]"
            >
              <div className="grid h-10 w-10 place-items-center rounded-xl bg-blue-500/15 text-sky-300 ring-1 ring-white/10 transition-transform duration-300 group-hover:-translate-y-0.5">
                <b.icon size={18} aria-hidden />
              </div>
              <h3 className="mt-3.5 text-sm font-semibold text-white sm:text-base">{b.title}</h3>
              <p className="mt-1 text-sm leading-relaxed text-slate-400">{b.desc}</p>
            </article>
          ))}
        </div>

        {/* And more + CTA strip */}
        <div className="reveal mt-4 flex flex-col items-start justify-between gap-4 rounded-2xl border border-dashed border-white/15 bg-white/[0.03] px-5 py-5 sm:flex-row sm:items-center sm:px-6">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-white/10 text-sky-300">
              <Sparkles size={16} aria-hidden />
            </div>
            <div>
              <p className="text-sm font-semibold text-white">And more on the way</p>
              <p className="mt-0.5 text-sm text-slate-400">
                New Premium tools ship continuously—your membership keeps pace.
              </p>
            </div>
          </div>
          <Link
            to={ctaTo}
            state={ctaState}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-sky-300 transition-all hover:gap-2.5 hover:text-sky-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950"
          >
            View Premium plans
            <ArrowRight size={15} aria-hidden />
          </Link>
        </div>
      </div>
    </section>
  );
}
